import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicAPI } from '../utils/api';
import { format } from 'date-fns';
import { FiDownload, FiCheck, FiArrowRight, FiCreditCard } from 'react-icons/fi';
import './PublicInvoice.css';

const PublicInvoice = () => {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [token]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await publicAPI.getInvoice(token);
      setInvoice(response.data.invoice);
    } catch (err) {
      setError(err.response?.data?.error || 'Invoice not found');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice?.currency || 'AED',
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'MMMM dd, yyyy');
  };

  const getStatusBadge = (status) => {
    // Only show Paid or Unpaid for public view
    return status === 'paid' ? 'badge-paid' : 'badge-overdue';
  };

  const getDisplayStatus = (status) => {
    // Only show Paid or Unpaid for public view
    return status === 'paid' ? 'Paid' : 'Unpaid';
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await publicAPI.getInvoicePDF(token);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to generate PDF');
    }
  };

  const handlePayNow = async () => {
    try {
      setPaying(true);
      const response = await publicAPI.createPayment(token);
      if (response.data.paymentUrl) {
        window.location.href = response.data.paymentUrl;
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to initiate payment');
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="public-loading">
        <div className="loading-spinner"></div>
        <p>Loading invoice...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-error">
        <div className="error-icon">!</div>
        <h2>Invoice Not Found</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="public-invoice">
      <div className="public-header">
        <div className="public-header-content">
          <div className="public-logo">
            <FiArrowRight className="logo-icon" />
            <span>iOnline ERP</span>
          </div>
          <div className="public-actions">
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FiDownload /> Download PDF
            </button>
          </div>
        </div>
      </div>

      <div className="public-content">
        <div className="invoice-document">
          {/* Invoice Header - Top Row: Logo + Company Name | Invoice Details */}
          <div className="invoice-header-top">
            <div className="invoice-logo-section">
              <img 
                src={invoice.company_logo || '/logo.svg'} 
                alt="Company Logo" 
                className="invoice-logo" 
              />
              <div className="invoice-company-name">{invoice.company_name || 'Your Company'}</div>
              {invoice.company_name_arabic && <div className="invoice-company-name-arabic">{invoice.company_name_arabic}</div>}
            </div>
            <div className="invoice-details-section">
              <h2 className="invoice-number">{invoice.invoice_number}</h2>
              <div className="detail-row">
                <span className="detail-label">Issue Date:</span>
                <span className="detail-value">{formatDate(invoice.issue_date)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Due Date:</span>
                <span className="detail-value">{formatDate(invoice.due_date)}</span>
              </div>
              {invoice.po_number && (
                <div className="detail-row">
                  <span className="detail-label">PO Number:</span>
                  <span className="detail-value">{invoice.po_number}</span>
                </div>
              )}
              <div className="detail-row status-row">
                <span className="detail-label">Status:</span>
                <span className={`status-badge ${invoice.status === 'paid' ? 'status-paid' : 'status-unpaid'}`}>
                  {getDisplayStatus(invoice.status)}
                </span>
              </div>
            </div>
          </div>

          {/* Divider Line */}
          <div className="header-divider"></div>

          {/* FROM & BILL TO Section - Side by Side */}
          <div className="invoice-parties">
            <div className="party-section">
              <h4 className="party-label">FROM</h4>
              <p className="party-name">{invoice.company_name || 'Your Company'}</p>
              {invoice.company_address && <p className="party-detail">{invoice.company_address}</p>}
              {(invoice.company_city || invoice.company_country) && (
                <p className="party-detail">{[invoice.company_city, invoice.company_country].filter(Boolean).join(', ')}</p>
              )}
              {invoice.company_email && <p className="party-detail">{invoice.company_email}</p>}
              {invoice.company_phone && <p className="party-detail">{invoice.company_phone}</p>}
            </div>
            <div className="party-section">
              <h4 className="party-label">BILL TO</h4>
              <p className="party-name">{invoice.client_company}</p>
              {invoice.client_contact && <p className="party-detail">{invoice.client_contact}</p>}
              {invoice.client_email && <p className="party-detail">{invoice.client_email}</p>}
              {invoice.client_address && <p className="party-detail">{invoice.client_address}</p>}
            </div>
          </div>

          {/* Items Table */}
          <div className="invoice-items">
            <table>
              <thead>
                <tr>
                  {invoice.display_fields?.sku && <th>SKU</th>}
                  {invoice.display_fields?.category && <th>Category</th>}
                  {invoice.display_fields?.item_name !== false && <th>Item Name</th>}
                  {invoice.display_fields?.description !== false && <th style={{ width: '40%' }}>Description</th>}
                  {invoice.display_fields?.unit_price && <th style={{ textAlign: 'right' }}>Unit Price</th>}
                  {invoice.display_fields?.unit && <th style={{ textAlign: 'center' }}>Unit</th>}
                  {invoice.display_fields?.quantity && <th style={{ textAlign: 'center' }}>Qty</th>}
                  {invoice.display_fields?.tax && <th style={{ textAlign: 'right' }}>Tax</th>}
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index}>
                    {invoice.display_fields?.sku && <td>{item.sku || '-'}</td>}
                    {invoice.display_fields?.category && <td>{item.category || '-'}</td>}
                    {invoice.display_fields?.item_name !== false && <td className="item-name">{item.name}</td>}
                    {invoice.display_fields?.description !== false && <td>{item.description || '-'}</td>}
                    {invoice.display_fields?.unit_price && <td style={{ textAlign: 'right' }}>{formatCurrency(item.unit_price)}</td>}
                    {invoice.display_fields?.unit && <td style={{ textAlign: 'center' }}>{item.unit || '-'}</td>}
                    {invoice.display_fields?.quantity && <td style={{ textAlign: 'center' }}>{item.quantity}</td>}
                    {invoice.display_fields?.tax && <td style={{ textAlign: 'right' }}>{item.tax_rate || 0}%</td>}
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(Number(item.total || 0) - Number(item.tax_amount || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="invoice-totals">
            <div className="total-row">
              <span>Subtotal</span>
              <span>{formatCurrency(invoice.subtotal)}</span>
            </div>
            {invoice.discount_amount > 0 && (
              <div className="total-row discount">
                <span>Discount</span>
                <span>-{formatCurrency(invoice.discount_amount)}</span>
              </div>
            )}
            <div className="total-row">
              <span>Tax</span>
              <span>{formatCurrency(invoice.tax_amount)}</span>
            </div>
            <div className="total-row grand-total">
              <span>Total</span>
              <span>{formatCurrency(invoice.total)}</span>
            </div>
            {invoice.amount_paid > 0 && (
              <>
                <div className="total-row">
                  <span>Amount Paid</span>
                  <span className="paid">-{formatCurrency(invoice.amount_paid)}</span>
                </div>
                <div className="total-row grand-total">
                  <span>Amount Due</span>
                  <span>{formatCurrency(invoice.amount_due)}</span>
                </div>
              </>
            )}
          </div>

          {/* Payment Schedule for Installments */}
          {invoice.payment_schedule && invoice.payment_schedule.length > 0 && (
            <div className="payment-schedule-section">
              <h4>Payment Schedule</h4>
              <table className="payment-schedule-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th>Percentage</th>
                    <th>Amount</th>
                    <th>Paid</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.payment_schedule.map((installment) => (
                    <tr key={installment.installment_number} className={installment.status === 'paid' ? 'paid' : ''}>
                      <td>{installment.installment_number}</td>
                      <td>{installment.description || `Installment ${installment.installment_number}`}</td>
                      <td>{installment.percentage}%</td>
                      <td>{formatCurrency(installment.total_amount)}</td>
                      <td>
                        {installment.status === 'partial' ? (
                          <span className="partial-amount">
                            {formatCurrency(installment.amount_paid)} / {formatCurrency(installment.total_amount)}
                          </span>
                        ) : installment.status === 'paid' ? (
                          <span className="fully-paid">{formatCurrency(installment.total_amount)}</span>
                        ) : (
                          <span className="not-paid">-</span>
                        )}
                      </td>
                      <td>
                        {installment.status === 'paid' ? (
                          <span className="installment-status paid">
                            <FiCheck /> Paid
                          </span>
                        ) : installment.status === 'partial' ? (
                          <span className="installment-status partial">
                            Partial ({formatCurrency(installment.amount_remaining)} remaining)
                          </span>
                        ) : (
                          <span className="installment-status pending">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pay Now Section */}
          {invoice.amount_due > 0 && invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
            <div className="pay-now-section">
              <div className="pay-now-card">
                <div className="pay-now-info">
                  <FiCreditCard className="pay-now-icon" />
                  <div>
                    <h4>Pay Online</h4>
                    <p>
                      {invoice.payment_schedule && invoice.next_installment_amount 
                        ? `Pay ${formatCurrency(invoice.next_installment_amount)} (Next Installment)` 
                        : `Pay ${formatCurrency(invoice.amount_due)}`} 
                      securely with Tap
                    </p>
                  </div>
                </div>
                <button 
                  className="btn btn-primary btn-lg" 
                  onClick={handlePayNow}
                  disabled={paying}
                >
                  {paying ? 'Processing...' : 'Pay Now'}
                </button>
              </div>
            </div>
          )}

          {/* Bank Details */}
          {invoice.show_bank_details && invoice.bank_account && (
            <div className="bank-details-section">
              <h4>Bank Details</h4>
              <div className="bank-details-card">
                <div className="bank-name">{invoice.bank_account.bank_name}</div>
                <div className="bank-detail-row">
                  <span className="bank-label">Account Name:</span>
                  <span className="bank-value">{invoice.bank_account.account_name}</span>
                </div>
                <div className="bank-detail-row">
                  <span className="bank-label">Account Number:</span>
                  <span className="bank-value">{invoice.bank_account.account_number}</span>
                </div>
                {invoice.bank_account.iban && (
                  <div className="bank-detail-row">
                    <span className="bank-label">IBAN:</span>
                    <span className="bank-value">{invoice.bank_account.iban}</span>
                  </div>
                )}
                {invoice.bank_account.swift_code && (
                  <div className="bank-detail-row">
                    <span className="bank-label">SWIFT/BIC:</span>
                    <span className="bank-value">{invoice.bank_account.swift_code}</span>
                  </div>
                )}
                {invoice.bank_account.branch_address && (
                  <div className="bank-detail-row">
                    <span className="bank-label">Branch:</span>
                    <span className="bank-value">{invoice.bank_account.branch_address}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes & Terms */}
          <div className="invoice-footer">
            {invoice.notes && (
              <div className="invoice-notes">
                <h4>Notes</h4>
                <p>{invoice.notes}</p>
              </div>
            )}
            {invoice.terms && (
              <div className="invoice-terms">
                <h4>Terms & Conditions</h4>
                <p>{invoice.terms}</p>
              </div>
            )}
          </div>

          {/* Payments */}
          {invoice.payments && invoice.payments.length > 0 && (
            <div className="invoice-payments">
              <h4>Payment History</h4>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Method</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{formatDate(payment.payment_date)}</td>
                      <td className="capitalize">{payment.payment_method.replace('_', ' ')}</td>
                      <td>{formatCurrency(payment.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="public-footer">
        <p>Powered by iOnline ERP - Professional Invoicing Solution</p>
      </div>
    </div>
  );
};

export default PublicInvoice;